"use client"

import React, { useState } from "react"
import { View, StyleSheet, Image, ScrollView, Pressable } from "react-native"
import { Text, useTheme, Divider, Button, SegmentedButtons, Menu, IconButton, Searchbar } from "react-native-paper"
import { File, FileText, ImageIcon, Grid, List, SortAsc, SortDesc, Filter } from "lucide-react"
import type { FileNode } from "../../../types/files"

interface FilePreviewProps {
  folder: FileNode | null
}

type ViewMode = "list" | "grid"
type SortField = "name" | "date" | "size"
type SortDirection = "asc" | "desc"

export function FilePreview({ folder }: FilePreviewProps) {
  const theme = useTheme()
  const [viewMode, setViewMode] = useState<ViewMode>("grid")
  const [sortField, setSortField] = useState<SortField>("name")
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc")
  const [searchQuery, setSearchQuery] = useState("")
  const [sortMenuVisible, setSortMenuVisible] = useState(false)
  const [filterMenuVisible, setFilterMenuVisible] = useState(false)
  const [fileTypeFilter, setFileTypeFilter] = useState<string | null>(null)
  const [filterMenuPosition, setFilterMenuPosition] = useState({ x: 0, y: 0 })
  const [sortMenuPosition, setSortMenuPosition] = useState({ x: 0, y: 0 })

  // Get all files in the selected folder
  const files = folder?.children?.filter((child) => child.type === "file") || []

  // Get unique file extensions for filtering
  const fileExtensions = React.useMemo(() => {
    const extensions = new Set<string>()
    files.forEach((file) => {
      const extension = file.name.split(".").pop()?.toLowerCase() || ""
      if (extension) extensions.add(extension)
    })
    return Array.from(extensions)
  }, [files])

  // Filter files based on search query and file type filter
  const filteredFiles = React.useMemo(() => {
    return files.filter((file) => {
      // Filter by search query
      const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase())

      // Filter by file type
      const extension = file.name.split(".").pop()?.toLowerCase() || ""
      const matchesFileType = fileTypeFilter ? extension === fileTypeFilter : true

      return matchesSearch && matchesFileType
    })
  }, [files, searchQuery, fileTypeFilter])

  // Sort files
  const sortedFiles = React.useMemo(() => {
    return [...filteredFiles].sort((a, b) => {
      let comparison = 0

      if (sortField === "name") {
        comparison = a.name.localeCompare(b.name)
      } else if (sortField === "date") {
        comparison = (a.modified || 0) - (b.modified || 0)
      } else if (sortField === "size") {
        comparison = (a.size || 0) - (b.size || 0)
      }

      return sortDirection === "asc" ? comparison : -comparison
    })
  }, [filteredFiles, sortField, sortDirection])

  // Format file size
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return "Unknown size"
    const units = ["B", "KB", "MB", "GB"]
    let size = bytes
    let unitIndex = 0
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024
      unitIndex++
    }
    return `${size.toFixed(2)} ${units[unitIndex]}`
  }

  // Format date
  const formatDate = (timestamp?: number) => {
    if (!timestamp) return "Unknown date"
    return new Date(timestamp * 1000).toLocaleString()
  }

  // Get file icon
  const getFileIcon = (fileName: string) => {
    const isPDF = fileName.toLowerCase().endsWith(".pdf")
    const isImage = fileName.match(/\.(jpg|jpeg|png|gif)$/i)
    const isJson = fileName.toLowerCase().endsWith(".json")

    if (isPDF) return <FileText size={viewMode === "grid" ? 32 : 20} color={theme.colors.primary} />
    if (isImage) return <ImageIcon size={viewMode === "grid" ? 32 : 20} color={theme.colors.primary} />
    if (isJson) return <File size={viewMode === "grid" ? 32 : 20} color={theme.colors.primary} />
    return <File size={viewMode === "grid" ? 32 : 20} color={theme.colors.primary} />
  }

  // Handle file click - open in new tab
  const handleFileClick = (file: FileNode) => {
    if (file.url) {
      window.open(file.url, "_blank")
    }
  }

  // Toggle sort direction
  const toggleSortDirection = () => {
    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
  }

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      padding: 16,
      backgroundColor: theme.colors.background,
    },
    toolbar: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: theme.colors.elevation.level1,
      borderRadius: 4,
      marginBottom: 16,
      overflow: "hidden",
    },
    filterControls: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      borderRightWidth: 1,
      borderRightColor: theme.colors.surfaceVariant,
    },
    viewControls: {
      flexDirection: "row",
      alignItems: "center",
    },
    searchBar: {
      height: 40,
      width: 240,
      backgroundColor: "transparent",
      borderRadius: 0,
      margin: 0,
    },
    emptyState: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
    },
    fileList: {
      flex: 1,
    },
    listItem: {
      flexDirection: "row",
      alignItems: "center",
      padding: 12,
      borderRadius: 8,
      marginBottom: 8,
    },
    listItemIcon: {
      marginRight: 16,
    },
    listItemContent: {
      flex: 1,
    },
    listItemName: {
      fontSize: 16,
      fontWeight: "500",
      marginBottom: 4,
    },
    listItemDetails: {
      flexDirection: "row",
      gap: 16,
    },
    listItemDetail: {
      fontSize: 12,
      opacity: 0.7,
    },
    fileGrid: {
      flex: 1,
    },
    gridContainer: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 16,
    },
    gridItem: {
      width: 160,
      height: 180,
      borderRadius: 8,
      padding: 16,
      alignItems: "center",
    },
    gridItemIcon: {
      height: 80,
      width: 80,
      justifyContent: "center",
      alignItems: "center",
      marginBottom: 12,
    },
    gridItemThumbnail: {
      width: 80,
      height: 80,
      borderRadius: 4,
    },
    gridItemName: {
      fontSize: 14,
      fontWeight: "500",
      textAlign: "center",
      marginBottom: 4,
    },
    gridItemDetail: {
      fontSize: 12,
      opacity: 0.7,
    },
  })

  if (!folder) {
    return (
      <View style={styles.container}>
        <Text style={{ color: theme.colors.onSurfaceVariant }}>Select a folder to view files</Text>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <View style={styles.toolbar}>
        <View style={styles.filterControls}>
          <View style={{ position: "relative" }}>
            <Button
              mode="text"
              style={{ borderRadius: 0, margin: 0 }}
              contentStyle={{ marginHorizontal: 4 }}
              icon={() => <Filter size={18} color={theme.colors.primary} />}
              onPress={(event) => {
                const target = event.currentTarget
                const rect = target.getBoundingClientRect()
                setFilterMenuPosition({ x: rect.left, y: rect.bottom })
                setFilterMenuVisible(true)
              }}
            >
              {fileTypeFilter || "Filter"}
            </Button>

            <Menu
              visible={filterMenuVisible}
              onDismiss={() => setFilterMenuVisible(false)}
              anchor={{ x: filterMenuPosition.x, y: filterMenuPosition.y }}
            >
              <Menu.Item
                onPress={() => {
                  setFileTypeFilter(null)
                  setFilterMenuVisible(false)
                }}
                title="All Files"
              />
              <Divider />
              {fileExtensions.map((ext) => (
                <Menu.Item
                  key={ext}
                  onPress={() => {
                    setFileTypeFilter(ext)
                    setFilterMenuVisible(false)
                  }}
                  title={`.${ext.toUpperCase()}`}
                />
              ))}
            </Menu>
          </View>

          <View style={{ position: "relative" }}>
            <Button
              mode="text"
              style={{ borderRadius: 0, margin: 0 }}
              contentStyle={{ marginHorizontal: 4 }}
              icon={() => <List size={18} color={theme.colors.primary} />}
              onPress={(event) => {
                const target = event.currentTarget
                const rect = target.getBoundingClientRect()
                setSortMenuPosition({ x: rect.left, y: rect.bottom })
                setSortMenuVisible(true)
              }}
            >
              Sort: {sortField}
            </Button>

            <Menu
              visible={sortMenuVisible}
              onDismiss={() => setSortMenuVisible(false)}
              anchor={{ x: sortMenuPosition.x, y: sortMenuPosition.y }}
            >
              <Menu.Item
                onPress={() => {
                  setSortField("name")
                  setSortMenuVisible(false)
                }}
                title="Name"
              />
              <Menu.Item
                onPress={() => {
                  setSortField("date")
                  setSortMenuVisible(false)
                }}
                title="Date"
              />
              <Menu.Item
                onPress={() => {
                  setSortField("size")
                  setSortMenuVisible(false)
                }}
                title="Size"
              />
            </Menu>
          </View>

          <Button
            mode="text"
            style={{ borderRadius: 0, margin: 0 }}
            contentStyle={{ marginHorizontal: 4 }}
            icon={() =>
              sortDirection === "asc" ? (
                <SortAsc size={18} color={theme.colors.primary} />
              ) : (
                <SortDesc size={18} color={theme.colors.primary} />
              )
            }
            onPress={toggleSortDirection}
          >
            {sortDirection === "asc" ? "Asc" : "Desc"}
          </Button>
        </View>

        <View style={styles.viewControls}>
          <SegmentedButtons
            style={{ margin: 0, borderRadius: 0 }}
            value={viewMode}
            onValueChange={(value) => setViewMode(value as ViewMode)}
            buttons={[
              {
                value: "list",
                icon: () => <List size={18} />,
                style: { borderRadius: 0 },
              },
              {
                value: "grid",
                icon: () => <Grid size={18} />,
                style: { borderRadius: 0 },
              },
            ]}
          />

          <Searchbar
            placeholder="Search files..."
            onChangeText={setSearchQuery}
            value={searchQuery}
            style={styles.searchBar}
            inputStyle={{ margin: 0 }}
          />
        </View>
      </View>

      {sortedFiles.length === 0 ? (
        <View style={styles.emptyState}>
          <Text style={{ color: theme.colors.onSurfaceVariant }}>
            {searchQuery || fileTypeFilter ? "No files match your search criteria" : "No files in this folder"}
          </Text>
        </View>
      ) : viewMode === "list" ? (
        <ScrollView style={styles.fileList}>
          {sortedFiles.map((file) => (
            <Pressable
              key={file.path}
              style={({ pressed }) => [
                styles.listItem,
                { backgroundColor: theme.colors.elevation.level1 },
                pressed && { opacity: 0.7 },
              ]}
              onPress={() => handleFileClick(file)}
            >
              <View style={styles.listItemIcon}>{getFileIcon(file.name)}</View>
              <View style={styles.listItemContent}>
                <Text style={styles.listItemName} numberOfLines={1}>
                  {file.name}
                </Text>
                <View style={styles.listItemDetails}>
                  <Text style={styles.listItemDetail}>{formatFileSize(file.size)}</Text>
                  <Text style={styles.listItemDetail}>{formatDate(file.modified)}</Text>
                </View>
              </View>
              <IconButton icon="open-in-new" size={20} onPress={() => handleFileClick(file)} />
            </Pressable>
          ))}
        </ScrollView>
      ) : (
        <ScrollView style={styles.fileGrid}>
          <View style={styles.gridContainer}>
            {sortedFiles.map((file) => (
              <Pressable
                key={file.path}
                style={({ pressed }) => [
                  styles.gridItem,
                  { backgroundColor: theme.colors.elevation.level1 },
                  pressed && { opacity: 0.7 },
                ]}
                onPress={() => handleFileClick(file)}
              >
                <View style={styles.gridItemIcon}>
                  {file.name.match(/\.(jpg|jpeg|png|gif)$/i) && file.url ? (
                    <Image source={{ uri: file.url }} style={styles.gridItemThumbnail} resizeMode="cover" />
                  ) : (
                    getFileIcon(file.name)
                  )}
                </View>
                <Text style={styles.gridItemName} numberOfLines={2}>
                  {file.name}
                </Text>
                <Text style={styles.gridItemDetail}>{formatFileSize(file.size)}</Text>
              </Pressable>
            ))}
          </View>
        </ScrollView>
      )}
    </View>
  )
}

